import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Field, LogoField, ORG_TYPE_LABEL, SectionCard, absoluteLogoUrl, publicLandingUrl, useDirtyTracker } from "./_shared";
import { QRBlock } from "./QRBlock";
import { MoveToGroupButton } from "./MoveToGroupButton";
import { RenameSlugButton } from "./RenameSlugButton";
import { Link2, ShieldCheck } from "lucide-react";

/* ---------------------------------------------------------------------------
 * Edit form for a single-level client (Independent Pub / Foundation / Charity).
 * Edits the client record + its 1 landing pub in one submit.
 * ------------------------------------------------------------------------- */
export function SingleLevelEditor({ client, pub, pubGroups = [], onSaved, onDeleted, onLive }) {
  const [name, setName] = useState(client.name || "");
  const [town, setTown] = useState(client.town || "");
  const [color, setColor] = useState(client.primary_color || pub?.primary_color || "#0038FF");
  const [contactName, setContactName] = useState(client.contact_name || "");
  const [contactPhone, setContactPhone] = useState(client.contact_phone || "");
  const [kpiUrl, setKpiUrl] = useState(client.kpi_url || "");
  const [notes, setNotes] = useState(client.notes || "");
  // iter143 — Automated Gameweek Engine (super-admin only). Stored on the
  // client so every sibling pub reads the same value.
  const [gameweek, setGameweek] = useState(client.current_gameweek_number ?? "");
  const [matchCount, setMatchCount] = useState(client.match_count ?? "");
  const [dayCount, setDayCount] = useState(client.day_count ?? "");
  const [saving, setSaving] = useState(false);
  // Show the PUB's logo — that's what /[slug] resolves and shows on the
  // public landing. Uploads target the pub endpoint below so the public
  // page and the admin editor are always in lock-step.
  const [logoUrl, setLogoUrl] = useState(pub?.logo_url || null);

  // iter76 — track unsaved edits on the details form. Amber pulse-dot on
  // the banner clears when we call `commit()` after a successful save.
  const {
    isDirty: detailsDirty,
    commit: commitDetails,
  } = useDirtyTracker({
    name, town, color, contactName, contactPhone, kpiUrl, notes, logoUrl,
  });

  // Bubble live-edit values up to the right-hand preview.
  useEffect(() => {
    onLive?.({ name, town, color, logoUrl, slug: pub?.slug });
  }, [name, town, color, logoUrl, pub?.slug, onLive]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/clients/${client.id}`, {
        name, town, contact_name: contactName, contact_phone: contactPhone,
        kpi_url: kpiUrl, notes, primary_color: color,
        // iter143 — blank input keeps the previous DB value untouched
        // (undefined skipped on the backend). Numeric input persists.
        current_gameweek_number: gameweek === "" ? null : Math.max(0, Number(gameweek) || 0),
        match_count: matchCount === "" ? null : Math.max(0, Number(matchCount) || 0),
        day_count: dayCount === "" ? null : Math.max(0, Number(dayCount) || 0),
      });
      if (pub?.id) {
        await api.put(`/pubs/${pub.id}`, {
          name, town, contact_name: contactName, contact_phone: contactPhone,
          kpi_url: kpiUrl, notes, primary_color: color,
        });
      }
      toast.success("Saved");
      commitDetails();
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete "${client.name}" and its landing page? Participants will be unlinked but their accounts stay.`)) return;
    try {
      await api.delete(`/clients/${client.id}`);
      toast.success("Deleted");
      onDeleted?.();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const logoUploadTarget = pub?.id ? `/pubs/${pub.id}/logo` : `/clients/${client.id}/logo`;
  const logoDeleteTarget = logoUrl && pub?.id ? `/pubs/${pub.id}/logo` : (logoUrl ? `/clients/${client.id}/logo` : null);

  const orgLabel = ORG_TYPE_LABEL[client.org_type] || "Client";
  // Public landing URL for the identity banner's "Open" action; admin
  // back-office URL for "Manage".
  const openHref = pub?.slug ? publicLandingUrl(pub.slug) : null;
  const manageHref = pub?.slug ? `/c/${pub.slug}/editor` : null;
  const bannerActions = (
    <>
      {openHref && (
        <a
          href={openHref}
          target="_blank"
          rel="noreferrer"
          title="Open public landing"
          data-testid={`single-level-open-${pub?.slug || client.slug || client.id}`}
          className="text-xs font-semibold text-zinc-700 hover:text-zinc-950 border border-zinc-200 hover:border-zinc-950 rounded-sm px-2 h-8 inline-flex items-center gap-1"
        >
          <Link2 className="h-3.5 w-3.5" /> Open
        </a>
      )}
      {manageHref && (
        <a
          href={manageHref}
          target="_blank"
          rel="noreferrer"
          title="Manage back office"
          data-testid={`single-level-manage-${pub?.slug || client.slug || client.id}`}
          className="text-xs font-semibold text-white bg-zinc-950 hover:bg-black rounded-sm px-2 h-8 inline-flex items-center gap-1"
        >
          <ShieldCheck className="h-3.5 w-3.5" /> Manage
        </a>
      )}
    </>
  );

  return (
    <form onSubmit={submit} className="space-y-6" data-testid={`edit-single-${client.id}`}>
      <SectionCard
        title={name || client.name || orgLabel}
        subtitle={`${town || "No town"} · /${pub?.slug || client.slug || ""}`}
        logoUrl={logoUrl ? absoluteLogoUrl(logoUrl) : null}
        accentColor={color}
        actions={bannerActions}
        testId="single-level-details-section"
        collapsible
        defaultCollapsed
        dirty={detailsDirty}
      >
        <LogoField
          current={logoUrl}
          endpointPost={logoUploadTarget}
          endpointDelete={logoDeleteTarget}
          onUpload={(data) => setLogoUrl(data.logo_url || null)}
          testId="client-logo"
        />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Name"><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-name" className="h-10 rounded-sm border-zinc-300" required /></Field>
          <Field label="Town"><Input value={town} onChange={(e) => setTown(e.target.value)} data-testid="edit-town" className="h-10 rounded-sm border-zinc-300" /></Field>
          <Field label="Slug" hint={`Landing URL: /${pub?.slug || "…"}`}>
            <div className="flex items-center gap-2">
              <Input readOnly value={pub?.slug || ""} className="h-10 rounded-sm border-zinc-200 bg-zinc-50 font-mono text-sm flex-1 min-w-0" data-testid="edit-slug" />
              {pub?.id && pub?.slug && (
                <RenameSlugButton pub={pub} onRenamed={onSaved} />
              )}
            </div>
          </Field>
          <Field label="Brand colour">
            <div className="flex items-center gap-2">
              <input type="color" value={color} onChange={(e) => setColor(e.target.value.toUpperCase())} className="h-10 w-14 rounded-sm border border-zinc-300 cursor-pointer p-0.5" />
              <Input value={color} onChange={(e) => setColor(e.target.value)} className="h-10 rounded-sm border-zinc-300 font-mono uppercase text-sm" data-testid="edit-color" />
            </div>
          </Field>
          <Field label="Contact name"><Input value={contactName} onChange={(e) => setContactName(e.target.value)} data-testid="edit-contact-name" className="h-10 rounded-sm border-zinc-300" /></Field>
          <Field label="Contact phone"><Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} data-testid="edit-contact-phone" className="h-10 rounded-sm border-zinc-300" /></Field>
          <Field label="KPI dashboard link" hint="URL to a Metabase / Looker / private KPI page">
            <Input value={kpiUrl} onChange={(e) => setKpiUrl(e.target.value)} placeholder="https://kpi.example.com/…" data-testid="edit-kpi" className="h-10 rounded-sm border-zinc-300" />
          </Field>
          <Field label="Notes" testId="edit-notes-field">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="edit-notes" className="min-h-[80px] rounded-sm border-zinc-300" />
          </Field>
        </div>

        {/* iter143 — Automated Gameweek Engine (super-admin only). Sits in
            its own labelled row so it never disappears into the details
            grid. Blank clears back to null (banner hides). */}
        <div className="pt-6 mt-2 border-t border-zinc-100">
          <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500 mb-3">
            Automated Gameweek Engine <span className="text-zinc-400 normal-case tracking-normal font-medium">· super-admin only</span>
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Current gameweek #" hint="Auto-increments on close season">
              <Input type="number" min="0" max="9999" value={gameweek} onChange={(e) => setGameweek(e.target.value)} data-testid="edit-gameweek" className="h-10 rounded-sm border-zinc-300 tabular-nums" />
            </Field>
            <Field label="Match count" hint="Default 6">
              <Input type="number" min="0" max="999" value={matchCount} onChange={(e) => setMatchCount(e.target.value)} data-testid="edit-match-count" className="h-10 rounded-sm border-zinc-300 tabular-nums" />
            </Field>
            <Field label="Day count" hint="Default 3">
              <Input type="number" min="0" max="999" value={dayCount} onChange={(e) => setDayCount(e.target.value)} data-testid="edit-day-count" className="h-10 rounded-sm border-zinc-300 tabular-nums" />
            </Field>
          </div>
        </div>

        {/* iter77 — QR + public link merged into the details section so
            each org type is a single expandable banner. Divider gives the
            block visual separation without adding another card. */}
        <div className="pt-6 mt-2 border-t border-zinc-100">
          <QRBlock slug={pub?.slug} name={name} />
        </div>
      </SectionCard>

      <div className="flex items-center justify-between pt-1">
        <button type="button" onClick={del} data-testid="edit-delete" className="text-xs font-semibold text-red-600 hover:text-red-700 inline-flex items-center gap-1">
          <Trash2 className="h-3.5 w-3.5" /> Delete record
        </button>
        <div className="flex items-center gap-3">
          {pub?.slug && pubGroups.length > 0 && (
            <MoveToGroupButton pub={pub} pubGroups={pubGroups} onMoved={onSaved} />
          )}
          <Button type="submit" disabled={saving} data-testid="edit-save" className="rounded-sm bg-zinc-950 hover:bg-black text-white h-10 px-6">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </form>
  );
}
